// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Body,
  Button,
  ButtonGroup,
  Column,
  InlineAlert,
  InputRadio,
  InputRadioGroup,
  LgPadding,
} from '@alto/design-system';
import { type ApiResponseRow } from '@alto/design-system/src/components/inputs/src/InputAutocomplete/types';
import { InputLabelRow } from '@alto/design-system/src/components/inputs/src/InputText/InputLabelRow';
import { AllergiesInput, MedicalConditionsInput, getAllergensForInput } from '@alto/features';
import React, { useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { clearUserError, fetchUsers, updateUser } from '~shared/actions/users';
import { GENDERS, NO_KNOWN_ALLERGIES, NO_KNOWN_MEDICAL_CONDITIONS } from '~shared/constants';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Gender } from '~shared/types';
import { OnboardingInsuranceActionSheet } from './OrganicOnboarding';

type Values = {
  allergens: string[];
  medical_conditions: string;
  gender?: Gender;
  hasAllergens?: boolean;
};

export const OnboardingHealthProfileForm = () => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const user = useSelectorShared(getCurrentUser);
  const error = useSelectorShared((state) => state.ui.errors.fetchUsersError || state.ui.errors.updateUserError);

  const handleSaveHealthProfile = async (values: Values) => {
    delete values.hasAllergens;
    if (values.allergens.length === 0) {
      values.allergens = [NO_KNOWN_ALLERGIES];
    }
    // eslint-disable-next-line import/no-deprecated
    const success = await dispatch(updateUser(values));
    if (success) {
      trackEvent({ event: EVENTS.ONBOARDING__HEALTH_PROFILE_COMPLETED });
      dispatch(clearUserError());
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchUsers());
      setActiveActionSheet(<OnboardingInsuranceActionSheet />, { dismissible: false });
    }
  };

  const {
    getValues,
    setValue,
    handleSubmit,
    control,
    formState: { submitCount },
  } = useForm<Values>({
    defaultValues: {
      allergens: getAllergensForInput(user),
      medical_conditions: user?.medical_conditions || NO_KNOWN_MEDICAL_CONDITIONS,
      gender: user?.gender ?? undefined,
    },
  });

  // the BE handles parsing free-form text allergies and structured allergens
  // submit all allergies through allergens field
  const allergensController = useController({
    control,
    name: 'allergens',
    rules: {
      validate: (value) => {
        return getValues('hasAllergens') && value.length === 0 ? 'Allergies cannot be blank' : true;
      },
    },
  });
  // controller to track whether yes/no selection for error reporting
  const hasAllergensController = useController({
    control,
    name: 'hasAllergens',
  });

  const medicalConditionsController = useController({
    control,
    name: 'medical_conditions',
    rules: { required: 'Medical conditions cannot be blank', minLength: 1 },
  });

  const genderController = useController({
    control,
    name: 'gender',
    rules: {
      required: 'Sex cannot be blank',
    },
  });

  const handleAllergiesChange = (allergies: ApiResponseRow<string>[]) => {
    const value = allergies.map((allergy) => allergy.value);
    allergensController.field.onChange(value);
  };

  return (
    <LgPadding>
      <Column gap="SM">
        <Body>Our pharmacists review every order, and use this info to ensure your well-being.</Body>
        {error ? (
          <InlineAlert type="warning">
            <Body>{getErrorMessageFromResponse({ error })}</Body>
          </InlineAlert>
        ) : null}
        <AllergiesInput
          allergies={allergensController.field.value ?? undefined}
          error={allergensController.fieldState.error?.message}
          isTouched={allergensController.fieldState.isTouched}
          handleChange={handleAllergiesChange}
          handleButtonChange={hasAllergensController.field.onChange}
          hasSubmitted={submitCount > 0}
          fullBleed
        />
        <MedicalConditionsInput
          medicalConditions={medicalConditionsController.field.value}
          handleChange={medicalConditionsController.field.onChange}
          error={medicalConditionsController.fieldState.error?.message}
          isTouched={medicalConditionsController.fieldState.isTouched}
          hasSubmitted={submitCount > 0}
          fullBleed
        />
        <InputLabelRow
          required
          labelID="gender"
          label="Sex"
          sublabel="This should match your insurance."
          error={genderController.fieldState.error?.message}
        />
        <InputRadioGroup
          onValueChange={(value) => {
            setValue('gender', value as Gender);
          }}
          defaultValue={getValues('gender')}
        >
          {Object.entries(GENDERS).map(([key, value]) => (
            <InputRadio
              label={value}
              value={key}
              key={key}
              radioPlacement="left"
            />
          ))}
        </InputRadioGroup>
        {genderController.fieldState.error?.message && (
          <Body color={COLORS.TEXT_COLORS.DANGER}>{genderController.fieldState.error.message}</Body>
        )}
        <ButtonGroup
          justifyButtons="right"
          buttons={[
            <Button
              key="next-button"
              label="Next"
              accessibilityLabel="Press to save medical info and continue"
              onPress={handleSubmit(handleSaveHealthProfile)}
            />,
          ]}
        />
      </Column>
    </LgPadding>
  );
};
